import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Styled components
const Container = styled.div`
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const Title = styled.h1`
  font-size: 24px;
  color: #333;
  margin: 0;
`;

const LogoutButton = styled.button`
  padding: 8px 16px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  padding: 15px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.active ? '#f39c12' : '#ddd'};
  background: ${props => props.active ? '#f39c12' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    border-color: #f39c12;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 15px;
  background: #f8f9fa;
  color: #333;
  font-weight: bold;
  border-bottom: 1px solid #dee2e6;
`;

const Td = styled.td`
  padding: 10px 15px;
  border-bottom: 1px solid #dee2e6;
  color: #333;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.85em;
  background: ${props => {
    switch (props.status) {
      case 'approved': return '#d4edda';
      case 'pending': return '#fff3cd';
      case 'rejected': return '#f8d7da';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'approved': return '#155724';
      case 'pending': return '#856404';
      case 'rejected': return '#721c24';
      default: return '#383d41';
    }
  }};
`;

const ActionButton = styled.button`
  padding: 5px 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 5px;
  background: ${props => props.variant === 'approve' ? '#28a745' : '#dc3545'};
  color: white;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MSTRAdminDashboard = () => {
  const [activeFilter, setActiveFilter] = useState('pending');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    const loggedIn = localStorage.getItem('adminLoggedIn');
    if (!loggedIn) {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Fetch submissions when filter changes
  useEffect(() => {
    if (localStorage.getItem('adminLoggedIn')) {
      fetchMSTRSubmissions();
    }
  }, [activeFilter]);

  const fetchMSTRSubmissions = async () => {
    setLoading(true);
    try {
      const apiUrl = `/api/admin/prediction_submissions?status=${activeFilter}&asset=mstr`;
      
      
      const response = await fetch(apiUrl);
      const data = await response.json();
      
      
      if (Array.isArray(data)) {
        setSubmissions(data);
      } else {
        console.error('Invalid response format:', data);
        setSubmissions([]);
      }
    } catch (error) {
      console.error('Error fetching MSTR submissions:', error);
      setSubmissions([]);
      setError('Failed to fetch MSTR submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (submissionId) => {
    try {
      const apiUrl = `/api/admin/mstr_prediction_submissions/${submissionId}/approve`;
      
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          admin_comment: 'MSTR prediction approved via MSTR admin dashboard'
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('MSTR submission approved successfully!');
        fetchMSTRSubmissions(); // Refresh the list
      } else {
        alert(`Failed to approve MSTR submission: ${data.message}`);
      }
    } catch (error) {
      console.error('Error approving MSTR submission:', error);
      alert('Error approving MSTR submission');
    }
  };

  const handleReject = async (submissionId) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;
    
    try {
      const hostname = window.location.hostname;
      const apiUrl = `http://${hostname}:3001/api/admin/submissions/${submissionId}`;
      
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'rejected',
          admin_comment: reason
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('MSTR submission rejected successfully!');
        fetchMSTRSubmissions();
      } else {
        alert(`Failed to reject MSTR submission: ${data.message}`);
      }
    } catch (error) {
      console.error('Error rejecting MSTR submission:', error);
      alert('Error rejecting MSTR submission');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    navigate('/admin/login');
  };

  const filterOptions = ['all', 'pending', 'approved', 'rejected'];

  return (
    <Container>
      <Header>
        <Title>MSTR Admin Dashboard - MicroStrategy Prediction Submissions</Title>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button 
            onClick={() => navigate('/admin/dashboard')}
            style={{
              padding: '8px 16px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Bitcoin Dashboard
          </button>
          <LogoutButton onClick={handleLogout}>
            Logout
          </LogoutButton>
        </div>
      </Header>

      {/* Status Filters */}
      <FilterContainer>
        <strong>Filter MSTR submissions by status: </strong>
        {filterOptions.map(filter => (
          <FilterButton
            key={filter}
            active={activeFilter === filter}
            onClick={() => setActiveFilter(filter)}
          >
            {filter.charAt(0).toUpperCase() + filter.slice(1)}
          </FilterButton>
        ))}
      </FilterContainer>

      {/* Error Message */}
      {error && (
        <div style={{ 
          padding: '15px', 
          background: '#fee', 
          border: '1px solid #fcc', 
          borderRadius: '4px',
          color: '#c33',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Loading or Submissions Table */}
      {loading ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          background: 'white',
          borderRadius: '8px'
        }}>
          Loading MSTR submissions...
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Expert Name</Th>
              <Th>MSTR Price Prediction</Th>
              <Th>Target Date</Th>
              <Th>Status</Th>
              <Th>Submitted</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {submissions.length === 0 ? (
              <tr>
                <Td colSpan={6} style={{ textAlign: 'center', padding: '40px' }}>
                  No MSTR submissions found for {activeFilter} status
                </Td>
              </tr>
            ) : (
              submissions.map((submission) => (
                <tr key={submission.id}>
                  <Td>{submission.expert_name}</Td>
                  <Td>{submission.price_prediction}</Td>
                  <Td>{new Date(submission.target_time).toLocaleDateString()}</Td>
                  <Td>
                    <StatusBadge status={submission.status}>
                      {submission.status}
                    </StatusBadge>
                  </Td>
                  <Td>{submission.submission_date ? new Date(submission.submission_date).toLocaleDateString() : 'N/A'}</Td>
                  <Td>
                    {submission.status === 'pending' && (
                      <>
                        <ActionButton 
                          variant="approve"
                          onClick={() => handleApprove(submission.id)}
                        >
                          Approve
                        </ActionButton>
                        <ActionButton 
                          variant="reject"
                          onClick={() => handleReject(submission.id)}
                        >
                          Reject
                        </ActionButton>
                      </>
                    )}
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* Instructions */}
      <div style={{ 
        marginTop: '20px', 
        padding: '15px', 
        background: 'white', 
        borderRadius: '8px',
        color: '#666',
        fontSize: '14px'
      }}>
        <strong>MSTR Dashboard Instructions:</strong>
        <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
          <li>This dashboard manages only MicroStrategy (MSTR) stock price predictions</li>
          <li>Use status filters to view specific types of MSTR submissions</li>
          <li>Approve MSTR submissions to import them as MSTR predictions</li>
          <li>Switch to Bitcoin Dashboard to manage Bitcoin predictions</li>
        </ul>
      </div>
    </Container>
  );
};

export default MSTRAdminDashboard;
